import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, Filter } from 'lucide-react'
import type { Column } from '@tanstack/react-table'
import { Button } from '@/shared/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover'
import { cn } from '@/shared/lib/utils'
import { TextFilter } from './filters/text-filter'
import { NumberFilter } from './filters/number-filter'
import { DateFilter } from './filters/date-filter'
import { SetFilter } from './filters/set-filter'
import { BooleanFilter } from './filters/boolean-filter'

interface DataTableColumnHeaderProps<TData, TValue> {
  column: Column<TData, TValue>
  title: string
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const { t } = useTranslation()
  const [filterOpen, setFilterOpen] = useState(false)
  const sorted = column.getIsSorted()
  const filtered = column.getIsFiltered()
  const filterVariant = column.columnDef.meta?.filterVariant
  // A centred column (a count, a row menu) centres its title over the values too.
  const centered = column.columnDef.meta?.align === 'center'

  return (
    <div className="flex items-center gap-0.5 overflow-hidden" title={column.columnDef.meta?.headerTooltip}>
      {column.getCanSort() ? (
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-7 px-2 text-xs font-medium text-muted-foreground hover:text-foreground min-w-0 flex-1 overflow-hidden',
            !centered && '-ml-2',
          )}
          onClick={() => column.toggleSorting(sorted === 'asc')}
        >
          <span className="truncate">{title}</span>
          {sorted === 'asc' ? (
            <ArrowUp className="ml-1 h-3 w-3 flex-shrink-0" />
          ) : sorted === 'desc' ? (
            <ArrowDown className="ml-1 h-3 w-3 flex-shrink-0" />
          ) : (
            <ArrowUpDown className="ml-1 h-3 w-3 opacity-40 flex-shrink-0" />
          )}
        </Button>
      ) : (
        <span className={cn('text-xs font-medium text-muted-foreground truncate min-w-0 flex-1', centered && 'text-center')}>
          {title}
        </span>
      )}

      {filterVariant && column.getCanFilter() && (
        <Popover open={filterOpen} onOpenChange={setFilterOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn('h-6 w-6 p-0 flex-shrink-0', filtered && 'text-primary')}
              aria-label={t('dataTable.filter')}
            >
              <Filter className={cn('h-3 w-3', filtered ? 'fill-current' : 'opacity-40')} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-auto" align="start">
            {filterVariant === 'text' && (
              <TextFilter column={column} onClose={() => setFilterOpen(false)} />
            )}
            {filterVariant === 'number' && (
              <NumberFilter column={column} onClose={() => setFilterOpen(false)} />
            )}
            {filterVariant === 'date' && (
              <DateFilter column={column} onClose={() => setFilterOpen(false)} />
            )}
            {filterVariant === 'set' && (
              <SetFilter column={column} onClose={() => setFilterOpen(false)} />
            )}
            {filterVariant === 'boolean' && (
              <BooleanFilter column={column} onClose={() => setFilterOpen(false)} />
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
